import type { Container, Sprite } from 'pixi.js';
import { FIREFLY as FIREFLY_KIND } from '../../sim/full/attacks/swarm';
import type { PropState } from '../../sim/state';
import type { ClassDrawing, PaintedDrawing } from '../drawings';
import { type Shape, TAU, paintSheet } from '../paint';
import type { Frame } from '../frame';
import type { Look } from '../player-look';
import type { Textures } from '../textures';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, hide, placeOutline, setTint } from '../util';
import { CAPE, HANDS, HOOD, LANTERN, UNIT, VEILLEUR_PIECES } from './veilleur-paint';

type Parts = Readonly<Record<keyof typeof VEILLEUR_PIECES, Shape>>;

const EXTENT = 44;
const MIDDLE = 2;
const ORBIT = {
  along: 21,
  alongStep: 6,
  jitter: 0.35,
  across: 17,
  acrossStep: 2.6,
  perBeat: 0.5,
  calm: 0.2,
  bob: 0.08,
} as const;
export const FIREFLY = {
  halo: 12,
  haloAlpha: 0.9,
  heart: 3,
  heartAlpha: 0.6,
  light: 0xfff6c0,
  twinkle: 0.25,
} as const;
const PORTRAIT_FIREFLIES = 8;
const LANTERN_GLOW = { low: 0.7, high: 0.3, beats: 2, sparks: 3, turn: 1.5, reach: 2.2 };
const MAX_FIREFLIES = 16;

export function orbitAt(
  rank: number,
  count: number,
  beats: number,
  calm: boolean,
): { along: number; across: number } {
  const angle =
    (rank * TAU) / count +
    ORBIT.jitter * Math.sin(rank * 2.3) +
    beats * ORBIT.perBeat * (calm ? ORBIT.calm : 1);
  const breath = 1 + ORBIT.bob * Math.sin(beats * 0.5 + rank * 1.7);
  return {
    along: Math.cos(angle) * (ORBIT.along + (rank % 2) * ORBIT.alongStep) * breath,
    across: Math.sin(angle) * (ORBIT.across + (rank % 3) * ORBIT.acrossStep) * breath,
  };
}

export function twinkle(rank: number, beats: number, calm: boolean): number {
  return calm ? 1 : 1 - FIREFLY.twinkle * (0.5 + 0.5 * Math.sin(beats * 1.3 + rank * 2.4));
}

export interface FireflySprites {
  readonly halo: Sprite;
  readonly heart: Sprite;
  readonly core: Sprite;
}

export function fireflySprites(
  glows: Container,
  bodies: Container,
  textures: Textures,
): FireflySprites {
  return {
    halo: add(glows, textures.halo),
    heart: add(glows, textures.halo),
    core: add(bodies, textures.players.downed.firefly),
  };
}

export function placeFirefly(
  sprites: FireflySprites,
  x: number,
  y: number,
  size: number,
  shine: number,
  frame: Frame,
  textures: Textures,
): void {
  const { halo, heart, core } = sprites;
  const { light, palette } = frame;
  halo.visible = heart.visible = core.visible = true;
  halo.position.set(x, y);
  halo.scale.set((FIREFLY.halo * UNIT * size) / textures.halo.radius);
  halo.alpha = FIREFLY.haloAlpha * shine * light.haloAlpha;
  setTint(halo, palette.noyau);
  heart.position.set(x, y);
  heart.scale.set((FIREFLY.heart * UNIT * size) / textures.halo.radius);
  heart.alpha = FIREFLY.heartAlpha * shine * light.haloAlpha;
  setTint(heart, FIREFLY.light);
  core.position.set(x, y);
  core.scale.set(size);
  core.alpha = shine;
  setTint(core, FIREFLY.light);
}

function createWatcherLook(
  bodies: Container,
  glows: Container,
  textures: Textures,
  parts: Parts,
): Look {
  const outline = add(bodies, parts.silhouette);
  const cape = add(bodies, parts.cape);
  const torso = add(bodies, parts.torso);
  const freeHand = add(bodies, parts.hand);
  const lanternGlow = add(glows, textures.halo);
  const holdingHand = add(bodies, parts.hand);
  const lantern = add(bodies, parts.lantern);
  const sparks = Array.from({ length: LANTERN_GLOW.sparks }, () => add(bodies, parts.spark));
  const hood = add(bodies, parts.hood);
  const swarm = Array.from({ length: MAX_FIREFLIES }, () =>
    fireflySprites(glows, bodies, textures),
  );
  const body = [cape, torso, freeHand, lantern, ...sparks, holdingHand, hood];
  const resting: PropState[] = [];
  let originX = 0;
  let originY = 0;
  let cos = 1;
  let sin = 0;
  let size = 1;
  let facing = 0;

  function put(sprite: Sprite, along: number, across: number, turn = 0): void {
    sprite.position.set(
      originX + (along * cos - across * sin) * UNIT * size,
      originY + (along * sin + across * cos) * UNIT * size,
    );
    sprite.rotation = facing + turn;
    sprite.scale.set(size);
  }

  return {
    hide() {
      hide(...body, outline, lanternGlow);
      for (const { halo, heart, core } of swarm) {
        hide(halo, heart, core);
      }
    },
    place(input) {
      const { player, state, x, y, angle, scale, color, frame } = input;
      const { now, calm, palette, light, tempo } = frame;
      const beats = now / tempo.ticksPerBeat;
      originX = x;
      originY = y;
      cos = Math.cos(angle);
      sin = Math.sin(angle);
      size = scale;
      facing = angle;
      for (const sprite of body) {
        sprite.visible = true;
      }

      put(cape, CAPE.along, 0);
      placeOutline(outline, cape, parts.silhouette.texture, REFERENCE_RADIUS, frame);
      put(torso, 0, 0);
      setTint(torso, color);
      put(freeHand, HANDS.free[0], HANDS.free[1]);
      setTint(freeHand, input.teint);

      const breath = 0.5 - 0.5 * Math.cos((TAU * beats) / LANTERN_GLOW.beats);
      lanternGlow.visible = true;
      put(lanternGlow, LANTERN.along, LANTERN.across);
      lanternGlow.scale.set((LANTERN.glow * UNIT * size) / textures.halo.radius);
      lanternGlow.alpha =
        light.haloAlpha * (LANTERN_GLOW.low + LANTERN_GLOW.high * (calm ? 0.5 : breath));
      setTint(lanternGlow, palette.noyau);
      put(lantern, LANTERN.along, LANTERN.across);
      setTint(lantern, 0xffffff);
      sparks.forEach((spark, index) => {
        const turn = beats * LANTERN_GLOW.turn * (calm ? ORBIT.calm : 1) + index * 2.1;
        put(
          spark,
          LANTERN.along + Math.cos(turn) * LANTERN_GLOW.reach,
          LANTERN.across + Math.sin(turn * 1.3) * LANTERN_GLOW.reach,
        );
        setTint(spark, FIREFLY.light);
      });
      put(holdingHand, HANDS.holding[0], HANDS.holding[1]);
      setTint(holdingHand, input.teint);
      put(hood, HOOD.along, 0);
      setTint(hood, 0xffffff);

      resting.length = 0;
      let count = PORTRAIT_FIREFLIES;
      if (!frame.portrait) {
        count = 0;
        for (const prop of state.props ?? []) {
          if (prop.kind === FIREFLY_KIND && prop.playerId === player.id) {
            count += 1;
            if (prop.phase === 'held') {
              resting.push(prop);
            }
          }
        }
      }
      const shown = frame.portrait ? PORTRAIT_FIREFLIES : resting.length;
      swarm.forEach((sprites, index) => {
        if (index >= shown) {
          hide(sprites.halo, sprites.heart, sprites.core);
          return;
        }
        const rank = frame.portrait ? index : (resting[index]?.rank ?? index);
        const { along, across } = orbitAt(rank, Math.max(1, count), beats, calm);
        placeFirefly(
          sprites,
          x + along * UNIT * size,
          y + across * UNIT * size,
          size,
          twinkle(rank, beats, calm),
          frame,
          textures,
        );
      });
      return 0;
    },
  };
}

export const VEILLEUR_DRAWING: ClassDrawing = {
  classId: 'veilleur',
  paint(density): PaintedDrawing {
    const { shapes, source } = paintSheet(VEILLEUR_PIECES, density);
    return {
      extent: EXTENT,
      height: 1,
      centerAhead: MIDDLE,
      shadow: shapes.shadow,
      source,
      create: (bodies: Container, glows: Container, textures: Textures) =>
        createWatcherLook(bodies, glows, textures, shapes),
    };
  },
};
